import { Pagination, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { DropdownOfTableButtons } from 'Components/ChecklistTables/ChecklistTableTeacher/ui';
import { useModal } from 'shared/hooks';
import { SendToStudentsModal } from 'shared/ui';

import { hwApi, subjectsApi } from '../../../api';
import checkSvg from '../../../assets/img/check-circle.svg';
import copySvg from '../../../assets/img/copy.svg';
import { getStudents } from '../../../Common/Database';
import { checklistTablePopupsNames } from '../../../constants/checklistTablePopupsNames';
import { tabs } from '../../../constants/checlistTableTabs';
import { tableSize } from '../../../constants/tableSize';
import { Option } from '../../../shared/types';
import { HW, PersonOption } from '../../../shared/types/works';
import { ItemActionModal } from '../../../shared/ui/ItemActionModal';
import { getTeachersStudent } from '../../../utils/getTeachersStudent';
import { getTeachersSubjects } from '../../../utils/getTeachersSubjects';
import { LoadingError } from '../../LoadingError';
import HomeworkFilters from '../HomeworkFilters';
import TableTabs from '../TableTabs';

interface Props {
  mail: string;
}

const teacherTabs = [
  { tabName: 'My templates', tabKey: tabs.myTemplates },
  { tabName: 'Students Works', tabKey: tabs.studentsWorks },
  { tabName: 'Templates', tabKey: tabs.template },
  { tabName: 'Archived', tabKey: tabs.archived },
];

export default function HomeworkTableTeacher({ mail }: Props) {
  const {
    t,
    i18n: { language: platformLang },
  } = useTranslation();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tabQuery = searchParams.get('tab');

  const [hws, setHws] = useState<(HW & { student?: PersonOption | null })[]>([]);
  const [hwsForShow, setHwsForShow] = useState<(HW & { student?: PersonOption | null })[]>([]);
  const [currentHw, setCurrentHw] = useState<HW | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRemovingLoading, setIsRemovingLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState('');
  const [idForManipulation, setIdForManipulation] = useState('');
  const [tab, setTab] = useState<string>(tabs.myTemplates);
  const [page, setPage] = useState(1);
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState<Option[] | null>(null);
  const [teachersSubjects, setTeachersSubjects] = useState<string[] | null>(null);
  const [click, setClick] = useState(false);

  const [subjectFilter, setSubjectFilter] = useState<Option | null>(null);
  const [studentFilter, setStudentFilter] = useState<PersonOption | null>(null);
  const [classFilter, setClassFilter] = useState<string | null>(null);

  const { isOpen, close, open } = useModal();

  useEffect(() => {
    setTab(tabQuery || tabs.myTemplates);
  }, [tabQuery]);

  useEffect(() => {
    getTeachersSubjects(mail).then(subjects => {
      setTeachersSubjects(subjects);
    });
  }, []);

  useEffect(() => {
    getTeachersStudent(mail).then(students => {
      setStudents(students);
    });
  }, []);

  useEffect(() => {
    if (hwsForShow.length === 0 && page > 1) {
      setPage(page - 1);
    }
  }, [hwsForShow]);

  useEffect(() => {
    if (teachersSubjects) {
      const updateSubjects = (subjects: Option[]) => {
        const filteredSubjects = subjects.filter(subject => teachersSubjects.includes(subject.name || subject.ruName));

        setSubjects(filteredSubjects);
      };
      const unsubscribe = subjectsApi.subscribeToChanges(updateSubjects);

      return () => {
        unsubscribe();
      };
    }
  }, [teachersSubjects]);

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);

      try {
        const res = getStudents();
        res.then(resp => {
          const arrStudents = Object.values(resp);
          setStudents(arrStudents);
        });
      } catch (error) {
        console.log(error);
      }

      setIsLoading(false);
    };

    init();
  }, []);

  useEffect(() => {
    setIsLoading(true);

    if (tab === tabs.template) {
      const unsubscribe = hwApi.subscribeToTemplateHWs({
        setNewHWs: setHws,
        subjectFilter: subjectFilter?.id,
        classFilter,
      });

      setIsLoading(false);

      return () => {
        unsubscribe();
      };
    }
    if (tab === tabs.myTemplates) {
      const unsubscribe = hwApi.subscribeToTeacherHWs({
        setNewHWs: setHws,
        teacherEmail: mail,
        studentFilter,
        subjectFilter: subjectFilter?.id,
        classFilter,
        myICLs: true,
      });
      setIsLoading(false);

      return () => {
        unsubscribe();
      };
    }
    if (tab === tabs.studentsWorks) {
      const unsubscribe = hwApi.subscribeToTeacherHWs({
        setNewHWs: setHws,
        teacherEmail: mail,
        studentFilter,
        subjectFilter: subjectFilter?.id,
        classFilter,
        studentsWorks: true,
      });
      setIsLoading(false);

      return () => {
        unsubscribe();
      };
    }
    if (tab === tabs.archived) {
      const unsubscribe = hwApi.subscribeToTeacherHWs({
        setNewHWs: setHws,
        teacherEmail: mail,
        isActive: false,
        studentFilter,
        subjectFilter: subjectFilter?.id,
        classFilter,
      });
      setIsLoading(false);

      return () => {
        unsubscribe();
      };
    }
  }, [tab, click]);

  useEffect(() => {
    setIsLoading(true);

    if (tab === tabs.template) {
      const unsubscribe = hwApi.subscribeToTemplateHWs({ setNewHWs: setHws });

      setIsLoading(false);

      return () => {
        unsubscribe();
      };
    }
    if (tab === tabs.myTemplates) {
      const unsubscribe = hwApi.subscribeToTeacherHWs({
        teacherEmail: mail,
        setNewHWs: setHws,
        myICLs: true,
      });
      setIsLoading(false);

      return () => {
        unsubscribe();
      };
    }
    if (tab === tabs.studentsWorks) {
      const unsubscribe = hwApi.subscribeToTeacherHWs({
        teacherEmail: mail,
        setNewHWs: setHws,
        studentsWorks: true,
      });
      setIsLoading(false);

      return () => {
        unsubscribe();
      };
    }
    if (tab === tabs.archived) {
      const unsubscribe = hwApi.subscribeToTeacherHWs({ setNewHWs: setHws, teacherEmail: mail, isActive: false });
      setIsLoading(false);

      return () => {
        unsubscribe();
      };
    }
  }, [tab]);

  useEffect(() => {
    const start = (page - 1) * tableSize;
    const end = start + tableSize;

    setHwsForShow(hws.slice(start, end));
  }, [hws, page, tab]);

  const handleNavigateToChecklistUpdate = (id: string) => {
    navigate('/hw-update', { state: { hwId: id } });
  };

  const handleNavigateToPreview = (id: string) => {
    navigate('/hw-preview', { state: { hwId: id } });
  };

  const handleDeleteICL = async (id: string) => {
    setIsRemovingLoading(true);

    try {
      await hwApi.removeHW(id);
    } catch (e) {
      console.log(e);
      toast.error(t('Something went wrong. Try again!'));
    }

    toast.success(t('Successfully removed!'));
    setIsModalOpen('');
    setIsRemovingLoading(false);
  };

  const handleOpenDeletePopup = (id: string) => {
    setIsModalOpen(checklistTablePopupsNames.delete);
    setIdForManipulation(id);
  };

  const handleArchivePopup = (icl: HW, isActive: boolean) => {
    setCurrentHw(icl);
    setIsModalOpen(isActive ? checklistTablePopupsNames.archive : checklistTablePopupsNames.activate);
  };

  const handleSendForConsideration = async (id: string) => {
    const targetIcl = hws.find(it => it.id === id);

    setIsLoading(true);

    if (targetIcl) {
      if (Object.prototype.hasOwnProperty.call(targetIcl, 'isConsideration')) {
        targetIcl.isConsideration = true;
      } else {
        targetIcl['isConsideration'] = true;
      }

      await hwApi.updateHW(targetIcl).then(() => {
        setIsLoading(false);
        toast.success(t('Successfully sent!'));
      });
    }
  };

  const changeICLStatus = async () => {
    setIsRemovingLoading(true);

    try {
      currentHw && (await hwApi.updateHW({ ...currentHw, isActive: !currentHw.isActive }));
    } catch (e) {
      console.log(e);
      toast.error(t('Something went wrong. Try again!'));
    }

    toast.success(currentHw?.isActive ? t('Successfully archived!') : t('Successfully activated!'));
    setIsModalOpen('');
    setIsRemovingLoading(false);
  };

  const handlePopupActions = async () => {
    switch (isModalOpen) {
      case 'delete':
        handleDeleteICL(idForManipulation);
        break;
      case 'copyAsTeacher':
        copyToTeacher(idForManipulation);
        break;
      case 'sendToAdmin':
        handleSendForConsideration(idForManipulation);
        break;
      case checklistTablePopupsNames.activate:
      case checklistTablePopupsNames.archive:
        await changeICLStatus();
        break;
    }

    setIsModalOpen('');
  };

  const actionPopupTitle = useMemo(() => {
    switch (isModalOpen) {
      case checklistTablePopupsNames.delete:
        return 'want to delete hw';
      case checklistTablePopupsNames.copyAsTeacher:
        return 'copyAsTether';
      case checklistTablePopupsNames.sendToAdmin:
        return 'sendToAdmin';
      case checklistTablePopupsNames.archive:
        return 'archive';
      case checklistTablePopupsNames.activate:
        return 'activate';
      default:
        return '';
    }
  }, [isModalOpen]);

  const actionPopupButtonTitle = useMemo(() => {
    switch (isModalOpen) {
      case checklistTablePopupsNames.delete:
        return 'delete';
      case checklistTablePopupsNames.copyAsTeacher:
        return 'copyAsTetherButton';
      case checklistTablePopupsNames.sendToAdmin:
        return 'sendToAdminButton';
      case checklistTablePopupsNames.archive:
        return 'archiveButton';
      case checklistTablePopupsNames.activate:
        return 'activateButton';
      default:
        return '';
    }
  }, [isModalOpen]);

  const copyToTeacher = async (id: string) => {
    setIsLoading(true);
    const targetIcl = hws.find(it => it.id === id);

    if (targetIcl) {
      await hwApi.copyHWToTeacher(targetIcl, mail).then(() => {
        setIsLoading(false);
        toast.success(t('Successfully copied!'));
      });
    }
  };

  const handleOpenCopyPopup = (id: string) => {
    setIsModalOpen(checklistTablePopupsNames.copyAsTeacher);
    setIdForManipulation(id);
  };

  const renderEditButton = (id: string, isTemplate: boolean) => {
    if (isTemplate) {
      return (
        <Tooltip
          title={t('copyAsTether')}
          componentsProps={{
            popper: {
              sx: {
                ['& .MuiTooltip-arrow']: {
                  color: '#9CDFFC',
                },
                ['& .MuiTooltip-tooltip']: {
                  backgroundColor: '#9CDFFC',
                  color: '#000000',
                  fontSize: '14px',
                  padding: '14px',
                  borderRadius: '8px',
                },
              },
            },
          }}
          arrow
        >
          <button
            className="btn more small h-[40px] w-[40px] rounded-[8px] !p-[6px]"
            onClick={() => handleOpenCopyPopup(id)}
          >
            <img src={copySvg} alt="copy" />
          </button>
        </Tooltip>
      );
    } else {
      return (
        tab !== tabs.archived && (
          <button className="btn btn-primary more" onClick={() => handleNavigateToChecklistUpdate(id)}>
            {t('editing')}
          </button>
        )
      );
    }
  };

  const renderPreviewButton = (id: string) => {
    if (tab === tabs.template) {
      return (
        <button className="btn btn-primary more" onClick={() => handleNavigateToPreview(id)}>
          {t('preview')}
        </button>
      );
    }
  };

  const handleOpenSendPopup = (id: string) => {
    setIsModalOpen(checklistTablePopupsNames.sendToAdmin);
    setIdForManipulation(id);
  };

  const openSendToStudentsModal = (icl: HW) => {
    setCurrentHw(icl);
    open();
  };

  const handleChangePage = async (newPage: number) => {
    setPage(newPage);
  };

  const getFIOFromEmail = (email: string) => {
    for (const student of students) {
      if (student.email?.replace(/\./g, '') === email) {
        return student.FIO;
      }
    }

    return email;
  };

  const renderRows = () => {
    return hwsForShow.map(hw => {
      const { id, subject, class: studentClass, isTemplate, studentEmail, title, checked, completed, student } = hw;

      return (
        <TableRow key={id}>
          <TableCell sx={{ maxWidth: 200, overflow: 'hidden', position: 'sticky', left: 0, backgroundColor: 'white' }}>
            {title}
          </TableCell>
          <TableCell sx={{ minWidth: 100 }}>{t(studentClass)}</TableCell>
          <TableCell>{platformLang === 'ua' ? subject.name : subject.ruName}</TableCell>
          {tab === tabs.studentsWorks && <TableCell>{student?.FIO ?? 'Loading...'}</TableCell>}
          {tab === tabs.studentsWorks && (
            <TableCell>
              {completed && <img className="m-auto h-[35px] w-[35px]" src={checkSvg} alt={t('completed')} />}
            </TableCell>
          )}
          {tab === tabs.studentsWorks && (
            <TableCell>
              {checked && <img className="m-auto h-[35px] w-[35px]" src={checkSvg} alt={t('checked')} />}
            </TableCell>
          )}
          {tab === tabs.studentsWorks ||
            (tab === tabs.archived && <TableCell>{getFIOFromEmail(studentEmail)}</TableCell>)}

          <TableCell>
            <div className="flex flex-row justify-end gap-[10px] [&>*]:!text-[16px] lg:[&>*]:!text-[18px]">
              {renderEditButton(id, isTemplate)}
              {tab !== tabs.template && (
                <Tooltip
                  title={t('deleteHomeWork')}
                  componentsProps={{
                    popper: {
                      sx: {
                        ['& .MuiTooltip-arrow']: {
                          color: '#9CDFFC',
                        },
                        ['& .MuiTooltip-tooltip']: {
                          backgroundColor: '#9CDFFC',
                          color: '#000000',
                          fontSize: '14px',
                          padding: '14px',
                          borderRadius: '8px',
                        },
                      },
                    },
                  }}
                  arrow
                >
                  <button
                    className="btn more small h-[40px] w-[40px] rounded-[8px]"
                    onClick={() => handleOpenDeletePopup(id)}
                  >
                    <span className="icon icon-delete text-red-600" />
                  </button>
                </Tooltip>
              )}

              {renderPreviewButton(id)}

              {tab !== tabs.template && (
                <DropdownOfTableButtons
                  icl={hw}
                  tab={tab}
                  isLoading={isLoading}
                  handleArchivePopup={handleArchivePopup}
                  handleOpenSendPopup={handleOpenSendPopup}
                  openSendToStudentsModal={openSendToStudentsModal}
                  hw
                />
              )}
            </div>
          </TableCell>
        </TableRow>
      );
    });
  };

  const handleApplyFilters = () => {
    setClick(!click);
  };

  const handleResetFilters = () => {
    setClick(!click);
    setStudentFilter(null);
    setSubjectFilter(null);
    setClassFilter(null);
  };

  const handleChangeTab = (tab: string) => {
    setTab(tab);
    handleResetFilters();
    setPage(1);
  };

  return (
    <LoadingError isLoading={isLoading && !hws}>
      <div className="lg:px-[15px]">
        <div className="mb-[20px] flex gap-[8px] lg:block">
          <TableTabs setTab={handleChangeTab} tab={tab} configArr={teacherTabs} />

          {students && subjects && (
            <HomeworkFilters
              subject={subjectFilter}
              student={studentFilter}
              studentClass={classFilter}
              setClassFilter={setClassFilter}
              setStudentFilter={setStudentFilter}
              setSubjectFilter={setSubjectFilter}
              students={students}
              handleApply={handleApplyFilters}
              handleReset={handleResetFilters}
              click={click}
              subjects={subjects}
              tab={tab}
            />
          )}
        </div>

        <div className="mb-[20px] rounded-[24px] bg-white [box-shadow:10px_10px_0px_0px_#EBEAE7] lg:px-[20px] lg:py-[15px]">
          <TableContainer sx={{ borderRadius: '24px' }}>
            <Table aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      maxWidth: 200,
                      position: 'sticky',
                      left: 0,
                      backgroundColor: 'white',
                      borderTopLeftRadius: '24px',
                    }}
                  >
                    {t('title')}
                  </TableCell>
                  <TableCell sx={{ minWidth: 100 }}>{t('class')}</TableCell>
                  <TableCell>{t('subject')}</TableCell>
                  {tab === tabs.studentsWorks && <TableCell>{t('FIO')}</TableCell>}
                  {tab === tabs.studentsWorks || (tab === tabs.archived && <TableCell>{t('alreadySend')}</TableCell>)}
                  {tab === tabs.studentsWorks && <TableCell>{t('completed')}</TableCell>}
                  {tab === tabs.studentsWorks && <TableCell>{t('checked')}</TableCell>}
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{renderRows()}</TableBody>
            </Table>
          </TableContainer>
        </div>

        <Stack alignItems="center">
          <Pagination
            sx={{
              '& .MuiPaginationItem-page.Mui-selected': {
                backgroundColor: 'white',
                border: '1px solid #52C7FA',
              },
              '& .MuiPaginationItem-page': {
                color: '#656D7B',
              },
            }}
            shape={'rounded'}
            page={page}
            onChange={(_, page) => handleChangePage(page)}
            count={Math.ceil(hws.length / tableSize)}
          />
        </Stack>

        <ItemActionModal
          isOpen={Boolean(isModalOpen)}
          close={() => setIsModalOpen('')}
          title={actionPopupTitle}
          onHandle={() => handlePopupActions()}
          isSubmitButtonDisabled={isRemovingLoading}
          buttonTitle={actionPopupButtonTitle}
        />
        <SendToStudentsModal currentICL={currentHw} isOpen={isOpen} onClose={close} />
      </div>
    </LoadingError>
  );
}
